import { useCallback, useEffect, useState } from 'react';
import apiClient from '../utils/api';

/**
 * The Sync Settings job toggles, flattened to `{ key: boolean }`.
 *
 * Two screens need these — a brand's Retail Connect tab, for the Accept and Push tracking row
 * actions, and Retailer Accounts, for Push inventory — so that a write button switched off in
 * Sync Settings says so instead of looking live and then failing. The settings endpoint serves
 * the toggles GROUPED by direction, which is right for the settings page and wrong for a
 * lookup, and that reshaping should not be written twice.
 *
 * `null` while loading AND if the fetch fails, and the consumer must treat null as "no reason
 * to stop": the server refuses the write on its own, so a settings call that did not come back
 * has no business disabling a button. Optimistic here, authoritative there.
 */
export function useJobToggles() {
  const [toggles, setToggles] = useState(null);

  const reloadToggles = useCallback(async () => {
    try {
      const data = await apiClient.getConnectorSettings();
      const flat = {};
      for (const group of data?.jobs || []) {
        // The server's own label travels with the state, so a button's explanation and the
        // refusal the route sends back name the setting identically. A label hardcoded here
        // drifted the moment "Accept orders automatically" became "Accept orders".
        for (const toggle of group.toggles || []) {
          flat[toggle.key] = { enabled: toggle.enabled, label: toggle.label };
        }
      }
      setToggles(Object.keys(flat).length > 0 ? flat : null);
    } catch {
      // Deliberately silent. This decides whether a button carries an explanation, not
      // whether the screen works, and a banner for it would be noise on every other screen.
      setToggles(null);
    }
  }, []);

  useEffect(() => {
    reloadToggles();
  }, [reloadToggles]);

  /**
   * The reason a write is unavailable, or null when there is none to give.
   *
   * `fallbackLabel` is only used if the settings call has not named this toggle — which
   * should not happen, and is not worth showing a key like `pushTrackingEnabled` for.
   */
  const switchedOffReason = useCallback((key, fallbackLabel) => {
    const entry = toggles?.[key];
    if (!entry || entry.enabled !== false) return null;
    return `${entry.label || fallbackLabel} is switched off in Sync Settings. `
      + 'Switch it on there to use this.';
  }, [toggles]);

  return { toggles, reloadToggles, switchedOffReason };
}

export default useJobToggles;
